import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";
import { ListFooter } from "components";
import { ListFilterBase, Permission, SimpleDto, ScholarshipTypeListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipTypeListPageHeader } from "./ScholarshipTypeListPageHeader";
import { ScholarshipTypeTable } from "./ScholarshipTypeTable";

const routeName = "scholarshipTypes";

export const scholarshipTypeListPageRoute: PageRoute = {
    handle: "Scholarship Types",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const ScholarshipTypeListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;
    const [listItems, setListItems] = useState<SimpleDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<ScholarshipTypeListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.scholarshipTypes.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.scholarshipTypes.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.scholarshipTypes.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<ScholarshipTypeListPageHeader rootPermission={ROOT_PERMISSION} handle={scholarshipTypeListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <ScholarshipTypeTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            scholarshipTypes={listItems}
                            setScholarshipTypes={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};